import { orderBy } from 'lodash';
import getImage from './getImage';
import isVoucherAvailableAt from './isVoucherAvailableAt';

const getBenefitLabel = (benefit, currency = '') => {
  const benefitType = benefit?.benefitType || '3';
  if (benefitType === '1') return `${Number(benefit.discountValue) || 0}% off`;
  if (benefitType === '2')
    return `${currency}${Number(benefit.discountValue) || 0} off`;
  return 'Free';
};

const getQuantity = (config) =>
  Number(config?.quantity) > 0 ? Math.floor(Number(config.quantity)) : 1;

export const getBadgeLabel = (voucher, currency = '') => {
  const { buyConfig, getConfig, benefit } = voucher;
  const buyLabel =
    buyConfig?.triggerType === 'spend'
      ? `Spend ${currency}${Number(buyConfig.minSpend) || 0}`
      : `Buy ${getQuantity(buyConfig)}`;
  const benefitType = benefit?.benefitType || '3';
  const discountValue = Number(benefit?.discountValue) || 0;

  if (benefitType === '1') return `${buyLabel} Get ${discountValue}% Off`;
  if (benefitType === '2')
    return `${buyLabel} Get ${currency}${discountValue} Off`;
  return `${buyLabel} Get ${getQuantity(getConfig)} Free`;
};

const getMinSizePrice = (selectedSizes = []) => {
  const prices = selectedSizes
    .map((size) => Number(size.price))
    .filter((price) => !Number.isNaN(price));
  return prices.length ? Math.min(...prices).toFixed(2) : '';
};

export default function getOffersSections({
  voucherGroups = [],
  vouchers: allVouchers = [],
  currency = '',
  checkMoment,
  timeZone,
}) {
  const vouchers = checkMoment
    ? allVouchers.filter((voucher) =>
        isVoucherAvailableAt(voucher, checkMoment, timeZone)
      )
    : allVouchers;
  const vouchersById = new Map(vouchers.map((v) => [v._id, v]));
  const groupedVoucherIds = new Set();

  const groupSections = orderBy(voucherGroups, ['displayOrder'], ['asc'])
    .map((group) => {
      const items = orderBy(group.vouchers ?? [], ['displayOrder'], ['asc'])
        .filter((groupVoucher) => vouchersById.has(groupVoucher.id))
        .map((groupVoucher) => {
          groupedVoucherIds.add(groupVoucher.id);
          const voucher = vouchersById.get(groupVoucher.id);
          return {
            key: groupVoucher.id,
            voucherId: groupVoucher.id,
            name: voucher.voucherHeading || voucher.voucherCode,
            subHeading: voucher.voucherSubHeading || '',
            imgSrc: getImage(groupVoucher.voucherImageUrl),
            badge: getBadgeLabel(voucher, currency),
            priceBadge: getBenefitLabel(voucher.benefit, currency),
          };
        });

      return {
        key: group._id,
        heading: group.heading,
        subHeading: group.subHeading,
        items,
      };
    })
    .filter((section) => section.items.length > 0);

  const voucherSections = vouchers
    .filter((voucher) => !groupedVoucherIds.has(voucher._id))
    .map((voucher) => {
      const heading = voucher.voucherHeading || voucher.voucherCode;
      const badge = getBadgeLabel(voucher, currency);
      return {
        key: voucher._id,
        heading,
        subHeading: voucher.voucherSubHeading,
        items: (voucher.buyConfig?.menuItems ?? []).map((menuItem) => {
          const price = getMinSizePrice(menuItem.selectedSizes);
          return {
            key: menuItem._id,
            voucherId: voucher._id,
            menuItemId: menuItem._id,
            name: menuItem.name,
            description: menuItem.description,
            imgSrc: getImage(menuItem.mediaPath || menuItem.mediaUrl),
            badge,
            priceBadge: price ? `${currency}${price}` : '',
          };
        }),
      };
    })
    .filter((section) => section.items.length > 0);

  return [...groupSections, ...voucherSections];
}
